<template>
  <div class="appointments-page">
    <div class="container container--fluid">
      <PageHeader :title="$t('appointments.title')"></PageHeader>

      <div class="appointments-page__content">
        <div class="calendar-container">
          <div id="calendar" ref="calendarEl"></div>
        </div>

        <div class="appointments-sidebar" v-if="selectedAppointment">
          <div class="appointment-details">
            <div class="appointment-details__header">
              <h3>{{ selectedAppointment.title }}</h3>
              <button @click="closeDetails" class="close-btn">
                <BaseIcon name="close" size="16" />
              </button>
            </div>

            <div class="appointment-details__content">
              <div class="detail-item">
                <strong>{{ $t('appointments.details.date_time') }}:</strong>
                <p>
                  {{ formatDateTime(selectedAppointment.start) }} -
                  {{ formatTime(selectedAppointment.end) }}
                </p>
              </div>

              <div class="detail-item">
                <strong>{{ $t('appointments.details.status') }}:</strong>
                <span :class="`status-badge status-badge--${selectedAppointment.status}`">
                  {{ getStatusLabel(selectedAppointment.status) }}
                </span>
              </div>

              <!-- Location Details -->
              <div
                class="detail-item"
                v-if="selectedAppointment.locationDetails || selectedAppointment.location"
              >
                <strong>{{ $t('appointments.details.location') }}:</strong>
                <div class="location-info">
                  <BaseIcon
                    :name="getLocationIcon(selectedAppointment.locationDetails?.type)"
                    size="16"
                  />
                  <div class="location-info__content">
                    <span class="location-info__name">
                      {{
                        selectedAppointment.locationDetails?.display || selectedAppointment.location
                      }}
                    </span>
                    <span
                      v-if="selectedAppointment.locationDetails?.type"
                      class="location-info__type"
                    >
                      {{ getLocationTypeLabel(selectedAppointment.locationDetails.type) }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="detail-item" v-if="selectedAppointment.description">
                <strong>{{ $t('appointments.details.description') }}:</strong>
                <p>{{ selectedAppointment.description }}</p>
              </div>

              <div class="detail-item" v-if="selectedAppointment.attendees?.length">
                <strong>{{ $t('appointments.details.attendees') }}:</strong>
                <ul class="attendees-list">
                  <li v-for="attendee in selectedAppointment.attendees" :key="attendee">
                    {{ attendee }}
                  </li>
                </ul>
              </div>

              <!-- Calendar/Integration Info -->
              <div class="detail-item" v-if="getCalendarInfo(selectedAppointment)">
                <strong>{{ $t('appointments.details.calendar') }}:</strong>
                <div class="calendar-info">
                  <BaseIcon name="calendar" size="16" />
                  <div class="calendar-info__content">
                    <span>{{ getCalendarInfo(selectedAppointment) }}</span>
                    <span
                      v-if="selectedAppointment.integration?.isRoundRobin"
                      class="calendar-info__badge"
                    >
                      Round Robin
                    </span>
                  </div>
                </div>
              </div>

              <!-- Calendar Event Link -->
              <div class="detail-item" v-if="selectedAppointment.calendarEvent?.link">
                <strong>{{ $t('appointments.details.calendar_event') }}:</strong>
                <a
                  :href="selectedAppointment.calendarEvent.link"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="calendar-event-link"
                >
                  <BaseIcon name="external-link" size="14" />
                  {{ $t('appointments.actions.view_in_calendar') }}
                </a>
              </div>

              <div class="detail-item">
                <LeadCard :lead="selectedAppointment.lead" />
              </div>
            </div>

            <div class="appointment-details__actions">
              <button
                v-if="selectedAppointment.status === 'cancelled'"
                @click="restoreAppointment()"
                class="btn btn--success btn--sm"
              >
                {{ $t('appointments.actions.restore') }}
              </button>
              <button
                v-if="selectedAppointment.status !== 'cancelled'"
                @click="updateStatus('cancelled')"
                class="btn btn--danger btn--sm"
              >
                {{ $t('appointments.actions.cancel') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="loading" class="loading-overlay">
        <LoadingSpinner />
      </div>
    </div>
  </div>
</template>

<script>
  import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue';
  import {
    createCalendar,
    createViewMonthGrid,
    createViewWeek,
    createViewDay,
  } from '@schedule-x/calendar';
  import { createEventsServicePlugin } from '@schedule-x/events-service';
  import '@schedule-x/theme-default/dist/index.css';

  // Import temporal polyfill
  import 'temporal-polyfill/global';
  import LeadCard from '@/components/partials/Lead/LeadCard.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import LoadingSpinner from '@shared/components/LoadingSpinner.vue';
  import PageHeader from '@/components/partials/PageHeader.vue';
  import { schedulingService } from '@/services/schedulingService';
  import { useI18n } from '@/composables/useI18n';

  export default {
    name: 'AppointmentsPage',
    components: {
      BaseIcon,
      LoadingSpinner,
      LeadCard,
      PageHeader,
    },
    setup() {
      const { t } = useI18n();
      const calendarEl = ref(null);
      const calendar = ref(null);
      const appointments = ref([]);
      const filteredAppointments = ref([]);
      const selectedAppointment = ref(null);
      const selectedStatus = ref('');
      const selectedForm = ref('');
      const loading = ref(false);

      // Calendar setup
      // Track loaded date ranges to avoid duplicate fetches
      const loadedRanges = ref(new Set());

      const toDateParts = (date) => {
        if (typeof date === 'string') {
          const [year, month, day] = date.split('T')[0].split('-').map(Number);
          return { year, month, day };
        }

        return {
          year: date.year,
          month: date.month,
          day: date.day,
        };
      };

      const toDateString = (date) => {
        const { year, month, day } = toDateParts(date);
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      };

      const getMonthKey = (year, month) => {
        return `${year}-${String(month).padStart(2, '0')}`;
      };

      const getMonthRange = (year, month) => {
        const lastDay = new Date(year, month, 0).getDate();
        return {
          startDate: `${year}-${String(month).padStart(2, '0')}-01`,
          endDate: `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}T23:59:59.999Z`,
        };
      };

      const getMonthIndex = (year, month) => year * 12 + (month - 1);

      const getYearMonthFromIndex = (index) => {
        const year = Math.floor(index / 12);
        const month = (index % 12) + 1;
        return { year, month };
      };

      const loadAppointmentsForMonth = async (year, month) => {
        const monthKey = getMonthKey(year, month);
        if (loadedRanges.value.has(monthKey)) return false;

        const { startDate, endDate } = getMonthRange(year, month);

        loadedRanges.value.add(monthKey);

        try {
          const response = await schedulingService.getAppointments({
            statuses: 'booked',
            startDate,
            endDate,
            limit: 1000,
          });
          const data = Array.isArray(response) ? response : response.appointments || [];

          // Merge new appointments, avoiding duplicates
          const existingIds = new Set(appointments.value.map((a) => a.id));
          const newAppointments = data.filter((a) => !existingIds.has(a.id));
          appointments.value = [...appointments.value, ...newAppointments];

          return newAppointments.length > 0;
        } catch (error) {
          console.error('Error loading appointments for month:', error);
          loadedRanges.value.delete(monthKey);
          return false;
        }
      };

      const loadAppointmentsForRange = async (startDate, endDate) => {
        const startParts = toDateParts(startDate);
        const endParts = toDateParts(endDate);

        const startIndex = getMonthIndex(startParts.year, startParts.month);
        const endIndex = getMonthIndex(endParts.year, endParts.month);

        // Also preload one month before and after visible range for smooth navigation.
        const fetchPromises = [];
        for (let monthIndex = startIndex - 1; monthIndex <= endIndex + 1; monthIndex += 1) {
          const { year, month } = getYearMonthFromIndex(monthIndex);
          fetchPromises.push(loadAppointmentsForMonth(year, month));
        }
        loading.value = true;
        try {
          const results = await Promise.all(fetchPromises);
          if (results.some(Boolean)) {
            filteredAppointments.value = appointments.value;
            updateCalendarEvents();
          }
        } finally {
          loading.value = false;
        }
      };

      const initializeCalendar = () => {
        if (!calendarEl.value) return;

        try {
          const eventsServicePlugin = createEventsServicePlugin();

          calendar.value = createCalendar(
            {
              selectedDate: Temporal.Now.plainDateISO(),
              views: [createViewMonthGrid(), createViewWeek(), createViewDay()],
              events: [],
              locale: 'nl-NL',
              timezone: 'Europe/Amsterdam',
              defaultView: createViewMonthGrid().name,
              isResponsive: false,
              dayBoundaries: {
                start: '06:00',
                end: '22:00',
              },
              callbacks: {
                // Keep full calendar controls available on small screens.
                isCalendarSmall: () => false,
                onEventClick: async (calendarEvent) => {
                  try {
                    loading.value = true;
                    const detailedAppointment = await schedulingService.getAppointmentById(
                      calendarEvent.id
                    );
                    selectedAppointment.value = detailedAppointment;
                  } catch (error) {
                    console.error('Error loading appointment details:', error);
                    const appointment = appointments.value.find(
                      (app) => app.id === calendarEvent.id
                    );
                    if (appointment) {
                      selectedAppointment.value = appointment;
                    }
                  } finally {
                    loading.value = false;
                  }
                },
                onRangeUpdate: async (range) => {
                  await loadAppointmentsForRange(
                    toDateString(range.start),
                    toDateString(range.end)
                  );
                },
              },
            },
            [eventsServicePlugin]
          );

          calendar.value.render(calendarEl.value);

          if (appointments.value.length > 0) {
            updateCalendarEvents();
          }
        } catch (error) {
          console.error('Error initializing calendar:', error);
        }
      };

      // Helper function to update calendar events
      const updateCalendarEvents = () => {
        if (calendar.value && calendar.value.eventsService) {
          try {
            // Snapshot IDs before removing to avoid iterating a live reactive array.
            const existingEventIds = calendar.value.eventsService.getAll().map((e) => e.id);
            console.log('[appointments] updateCalendarEvents: removing', existingEventIds.length, 'events, adding', filteredAppointments.value.length);
            existingEventIds.forEach((id) => {
              calendar.value.eventsService.remove(id);
            });

            // Add new events one by one using the correct API
            filteredAppointments.value.forEach((appointment) => {
              // Convert to Temporal objects that Schedule-X expects
              // Handle ISO timestamps from API (e.g., "2025-10-16T07:10:00+00:00")
              let startDateTime, endDateTime;

              if (appointment.start.includes('T')) {
                // ISO format from API - convert from UTC to Europe/Amsterdam timezone
                // Convert "2025-10-16T07:10:00+00:00" to "2025-10-16T07:10:00[UTC]"
                const startISO = appointment.start.replace('+00:00', '[UTC]');
                const endISO = appointment.end.replace('+00:00', '[UTC]');

                // Parse as UTC and convert to Amsterdam timezone
                const startUTC = Temporal.ZonedDateTime.from(startISO);
                const endUTC = Temporal.ZonedDateTime.from(endISO);

                startDateTime = startUTC.withTimeZone('Europe/Amsterdam');
                endDateTime = endUTC.withTimeZone('Europe/Amsterdam');
              } else {
                // Legacy format "YYYY-MM-DD HH:mm" - assume already in local timezone
                startDateTime = Temporal.PlainDateTime.from(
                  `${appointment.start.replace(' ', 'T')}:00`
                ).toZonedDateTime('Europe/Amsterdam');
                endDateTime = Temporal.PlainDateTime.from(
                  `${appointment.end.replace(' ', 'T')}:00`
                ).toZonedDateTime('Europe/Amsterdam');
              }

              const event = {
                id: appointment.id,
                title: appointment.title,
                start: startDateTime,
                end: endDateTime,
                calendarId: appointment.status, // Use status as calendarId for color coding
              };

              calendar.value.eventsService.add(event);
            });
          } catch (error) {
            console.error('Error updating calendar events:', error);
          }
        } else {
          console.log('Calendar or eventsService not available yet');
        }
      };

      // Data loading
      const loadAppointments = async () => {
        // Load current month plus neighbors to avoid empty navigation steps.
        const today = Temporal.Now.plainDateISO();
        const startDate = `${today.year}-${String(today.month).padStart(2, '0')}-01`;
        const currentMonthLastDay = new Date(today.year, today.month, 0).getDate();
        const endDate = `${today.year}-${String(today.month).padStart(2, '0')}-${String(currentMonthLastDay).padStart(2, '0')}`;
        await loadAppointmentsForRange(startDate, endDate);
      };

      // Filtering
      const filterAppointments = () => {
        let filtered = [...appointments.value];

        if (selectedStatus.value) {
          filtered = filtered.filter((app) => app.status === selectedStatus.value);
        }

        if (selectedForm.value) {
          filtered = filtered.filter((app) => app.formId === selectedForm.value);
        }

        filteredAppointments.value = filtered;

        // Update calendar
        updateCalendarEvents();
      };

      // Actions
      const refreshAppointments = () => {
        loadAppointments();
      };

      const exportAppointments = () => {
        // Implementeer export functionaliteit
        const csvContent =
          'data:text/csv;charset=utf-8,' +
          'Titel,Datum,Tijd,Status,Locatie,Beschrijving\n' +
          filteredAppointments.value
            .map(
              (app) =>
                `"${app.title}","${formatDate(app.start)}","${formatTime(app.start)}-${formatTime(app.end)}","${app.status}","${app.location || ''}","${app.description || ''}"`
            )
            .join('\n');

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', 'afspraken.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };

      const updateStatus = async (newStatus) => {
        if (!selectedAppointment.value) return;

        try {
          loading.value = true;
          await schedulingService.updateAppointmentStatus(selectedAppointment.value.id, newStatus);

          // Update local data
          const index = appointments.value.findIndex(
            (app) => app.id === selectedAppointment.value.id
          );
          if (index !== -1) {
            appointments.value[index].status = newStatus;
            selectedAppointment.value.status = newStatus;
          }

          filterAppointments();
        } catch (error) {
          console.error('Error updating appointment status:', error);
        } finally {
          loading.value = false;
        }
      };

      const restoreAppointment = async () => {
        if (!selectedAppointment.value) return;

        try {
          loading.value = true;
          const restoredAppointment = await schedulingService.restoreAppointment(
            selectedAppointment.value.id
          );

          // Update local data with restored appointment info
          const index = appointments.value.findIndex(
            (app) => app.id === selectedAppointment.value.id
          );
          if (index !== -1) {
            appointments.value[index] = {
              ...appointments.value[index],
              status: 'booked',
              calendarEventId: restoredAppointment.calendarEventId,
              calendarEventLink: restoredAppointment.calendarEventLink,
            };
            selectedAppointment.value = {
              ...selectedAppointment.value,
              status: 'booked',
              calendarEventId: restoredAppointment.calendarEventId,
              calendarEventLink: restoredAppointment.calendarEventLink,
            };
          }

          filterAppointments();
        } catch (error) {
          console.error('Error restoring appointment:', error);
          alert(t('appointments.errors.restore_failed'));
        } finally {
          loading.value = false;
        }
      };

      const closeDetails = () => {
        selectedAppointment.value = null;
      };

      // Utility functions
      const formatDateTime = (dateTimeString) => {
        const date = new Date(dateTimeString);
        return date.toLocaleDateString('nl-NL', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false, // Use 24-hour format
          timeZone: 'Europe/Amsterdam',
        });
      };

      const formatDate = (dateTimeString) => {
        const date = new Date(dateTimeString);
        return date.toLocaleDateString('nl-NL', {
          timeZone: 'Europe/Amsterdam',
        });
      };

      const formatTime = (dateTimeString) => {
        const date = new Date(dateTimeString);
        return date.toLocaleTimeString('nl-NL', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false, // Use 24-hour format
          timeZone: 'Europe/Amsterdam',
        });
      };

      const getStatusLabel = (status) => {
        const statusKey = `appointments.statuses.${status}`;
        return t(statusKey) || status;
      };

      // Get calendar/integration info for appointments
      const getCalendarInfo = (appointment) => {
        if (!appointment) return null;

        // New API format with integration object
        if (appointment.integration?.name) {
          return appointment.integration.name;
        }

        // Check various possible field names for calendar/integration info (legacy)
        if (appointment.integration_name) {
          return appointment.integration_name;
        }
        if (appointment.calendar_name) {
          return appointment.calendar_name;
        }
        if (appointment.assigned_to_name) {
          return appointment.assigned_to_name;
        }
        if (appointment.round_robin_info?.integration_name) {
          return appointment.round_robin_info.integration_name;
        }
        if (appointment.round_robin_info?.assignee_name) {
          return appointment.round_robin_info.assignee_name;
        }

        return null;
      };

      // Get icon for location type
      const getLocationIcon = (type) => {
        switch (type) {
          case 'online':
            return 'video';
          case 'custom':
            return 'home';
          case 'fixed':
            return 'building';
          default:
            return 'location';
        }
      };

      // Get label for location type
      const getLocationTypeLabel = (type) => {
        const labels = {
          online: t('appointments.location_types.online') || 'Online meeting',
          fixed: t('appointments.location_types.fixed') || 'Vaste locatie',
          custom: t('appointments.location_types.custom') || 'Klantlocatie',
        };
        return labels[type] || type;
      };

      // Lifecycle
      onMounted(async () => {
        await nextTick();
        initializeCalendar();
        await loadAppointments();
      });

      onUnmounted(() => {
        if (calendar.value) {
          calendar.value.destroy();
        }
      });

      return {
        calendarEl,
        appointments,
        filteredAppointments,
        selectedAppointment,
        selectedStatus,
        selectedForm,
        loading,
        refreshAppointments,
        exportAppointments,
        updateStatus,
        restoreAppointment,
        closeDetails,
        filterAppointments,
        updateCalendarEvents,
        formatDateTime,
        formatDate,
        formatTime,
        getStatusLabel,
        getCalendarInfo,
        getLocationIcon,
        getLocationTypeLabel,
      };
    },
  };
</script>

<style lang="scss" scoped>
  .appointments-page {
    // padding: 2rem;
    display: flex;
    flex-direction: column;
    position: relative;

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 2rem;
    }

    &__title {
      font-size: 2rem;
      font-weight: 700;
      color: var(--slate-900);
      margin: 0;
    }

    &__actions {
      display: flex;
      gap: 1rem;
    }

    &__filters {
      display: flex;
      gap: 2rem;
      margin-bottom: 2rem;
      padding: 1rem;
      background: var(--slate-50);
      border-radius: 8px;
      border: 1px solid var(--slate-200);
    }

    &__content {
      display: flex;
      gap: 2rem;
      flex: 1;
      min-height: 0;
    }
  }

  .filter-group {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .filter-label {
    font-weight: 500;
    color: var(--slate-700);
    font-size: 14px;
  }

  .filter-select {
    padding: 0.5rem;
    border: 1px solid var(--slate-300);
    border-radius: 6px;
    background: white;
    font-size: 14px;
    min-width: 150px;

    &:focus {
      outline: none;
      border-color: var(--blue-500);
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
  }

  .calendar-container {
    flex: 1;
    min-height: 600px;
    background: white;
    border-radius: 8px;
    border: 1px solid var(--slate-200);
    overflow: hidden;
  }

  #calendar {
    height: 100%;
    width: 100%;
  }

  .appointments-sidebar {
    width: 350px;
    background: white;
    border-radius: 8px;
    border: 1px solid var(--slate-200);
    overflow: hidden;
  }

  @include lg-down {
    .appointments-page {
      &__content {
        flex-direction: column;
        gap: 1rem;
      }
    }

    .calendar-container {
      min-height: 520px;
    }

    .appointments-sidebar {
      width: 100%;
    }
  }

  .appointment-details {
    height: 100%;
    display: flex;
    flex-direction: column;

    &__header {
      padding: 1.5rem;
      border-bottom: 1px solid var(--slate-200);
      display: flex;
      justify-content: space-between;
      align-items: flex-start;

      h3 {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 600;
        color: var(--slate-900);
        line-height: 1.4;
      }
    }

    &__content {
      padding: 1.5rem;
      flex: 1;
      overflow-y: auto;
    }

    &__actions {
      padding: 1.5rem;
      border-top: 1px solid var(--slate-200);
      display: flex;
      gap: 1rem;
    }
  }

  .close-btn {
    background: none;
    border: none;
    color: var(--slate-400);
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: var(--slate-100);
      color: var(--slate-600);
    }
  }

  .detail-item {
    margin-bottom: 1.5rem;

    &:last-child {
      margin-bottom: 0;
    }

    strong {
      display: block;
      font-weight: 600;
      color: var(--slate-900);
      margin-bottom: 0.5rem;
      font-size: 14px;
    }

    p {
      margin: 0;
      color: var(--slate-700);
      line-height: 1.5;
    }
  }

  .status-badge {
    display: inline-block;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.025em;

    &--booked {
      background: var(--green-100);
      color: var(--green-600);
    }
  }

  .attendees-list {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      padding: 0.25rem 0;
      color: var(--slate-700);
    }
  }

  .location-info {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: var(--slate-50);
    border-radius: 6px;
    border: 1px solid var(--slate-200);
    color: var(--slate-700);
    font-size: 14px;

    :deep(.icon) {
      color: var(--slate-500);
      flex-shrink: 0;
      margin-top: 2px;
    }

    &__content {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    &__name {
      color: var(--slate-700);
    }

    &__type {
      font-size: 12px;
      color: var(--slate-500);
    }
  }

  .calendar-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: var(--slate-50);
    border-radius: 6px;
    border: 1px solid var(--slate-200);
    color: var(--slate-700);
    font-size: 14px;

    :deep(.icon) {
      color: var(--slate-500);
      flex-shrink: 0;
    }

    &__content {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    &__badge {
      display: inline-flex;
      align-items: center;
      padding: 0.125rem 0.5rem;
      background: var(--blue-100);
      color: var(--blue-700);
      border-radius: 9999px;
      font-size: 11px;
      font-weight: 500;
    }
  }

  .calendar-event-link {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.5rem 0.75rem;
    background: var(--blue-50);
    color: var(--blue-600);
    border-radius: 6px;
    border: 1px solid var(--blue-200);
    font-size: 14px;
    text-decoration: none;
    transition: all 0.15s ease;

    &:hover {
      background: var(--blue-100);
      border-color: var(--blue-300);
    }
  }

  .btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-radius: 6px;
    border: 1px solid transparent;
    font-weight: 500;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
    text-decoration: none;

    &--primary {
      background: var(--blue-600);
      color: white;

      &:hover {
        background: var(--blue-700);
      }
    }

    &--secondary {
      background: white;
      color: var(--slate-700);
      border-color: var(--slate-300);

      &:hover {
        background: var(--slate-50);
        border-color: var(--slate-400);
      }
    }

    &--success {
      background: var(--green-600);
      color: white;

      &:hover {
        background: var(--green-700);
      }
    }

    &--danger {
      background: var(--red-600);
      color: white;

      &:hover {
        background: var(--red-700);
      }
    }

    &--sm {
      padding: 0.5rem 0.75rem;
      font-size: 13px;
    }
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  // Schedule-X theme overrides using our CSS variables
  :global(:root) {
    --sx-color-primary: var(--brand-600);
    --sx-color-on-primary: var(--white);
    --sx-color-primary-container: var(--brand-100);
    --sx-color-on-primary-container: var(--brand-800);
    --sx-color-secondary: var(--slate-600);
    --sx-color-on-secondary: var(--white);
    --sx-color-secondary-container: var(--slate-100);
    --sx-color-on-secondary-container: var(--slate-800);
    --sx-color-tertiary: var(--emerald-600);
    --sx-color-on-tertiary: var(--white);
    --sx-color-tertiary-container: var(--emerald-100);
    --sx-color-on-tertiary-container: var(--emerald-700);
    --sx-color-surface: var(--white);
    --sx-color-surface-dim: var(--slate-100);
    --sx-color-surface-bright: var(--white);
    --sx-color-on-surface: var(--slate-900);
    --sx-color-surface-container: var(--slate-50);
    --sx-color-surface-container-low: var(--slate-50);
    --sx-color-surface-container-high: var(--slate-100);
    --sx-color-background: var(--white);
    --sx-color-on-background: var(--slate-900);
    --sx-color-outline: var(--slate-300);
    --sx-color-outline-variant: var(--slate-200);
    --sx-color-shadow: var(--slate-900);
    --sx-color-surface-tint: var(--brand-600);
    --sx-color-neutral: var(--slate-400);
    --sx-color-neutral-variant: var(--slate-300);

    // Internal Schedule-X colors
    --sx-internal-color-gray-ripple-background: var(--slate-200);
    --sx-internal-color-light-gray: var(--slate-50);
    --sx-internal-color-text: var(--slate-900);
  }
</style>
